import * as api from './api';
import * as types from './mutations';

const shouldGetCatalogueFromCache = (state, params) => {
    return !! state.catalogue[params.status].data.length && ! params.loadMore;
};

const state = {
    isProcessing: false,
    enrolledCourses: {},
    completedCourses: [],
    coursesEnrolled: [],
    catalogue: {
        inProgress: {
            data: [],
            pagination: {}
        },
        completed: {
            data: [],
            pagination: {}
        },
        archived: {
            data: [],
            pagination: {}
        },
    },
    results: {
        data: [],
        pagination: {}
    },
    orderBy: 'updated_at'
};

const getters = {
    learningProgress(state) {
        return function (resourceType, course) {
            return state[resourceType][course];
        }
    },

    allEnrolledCourses(state) {
        return function () {
            return state.coursesEnrolled
        }
    }
};

const actions = {
    getCoursesEnrolled({commit, state, dispatch}) {
        return new Promise((resolve, reject) => {
            axios.get(api.getCoursesEnrolled())
                .then((data) => {
                    const courses = data.data.results;

                    commit(types.SET_COURSES_ENROLLED, courses);

                    resolve(courses);
                })
                .catch(({response}) => reject(response))
                .done();
        });
    },

    /**
     * Get member course progress.
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param rootState
     * @param courseSlug
     * @returns {Promise}
     */
    getCourseProgress({commit, state, dispatch, rootState}, {courseSlug}) {
        return new Promise((resolve, reject) => {
            /**
             * If we've already fetched enrolled course then we should load from caching.
             * We don't have access to course content so we should get from root state,
             * which was stored previously on first fetch.
             */
            if (state.enrolledCourses[courseSlug]) {
                const memberCourse = state.enrolledCourses[courseSlug];
                const course = rootState.course.courseContent[courseSlug];

                if (!memberCourse.isActive && !memberCourse.completedAt) {
                    return reject({data: {isActive: false}})
                }

                if (memberCourse.status === 'In Progress') {
                    dispatch('member.details/saveRecentViewedCourse', {course}, {root: true});
                }

                return resolve({cache: true});
            }

            commit(types.SET_PROCESSING_STATE, true);

            axios.get(api.getCourseProgress(courseSlug))
                .then(({data}) => {
                    const memberCourse = data.memberCourse.data;
                    const course = data.course.data;

                    if (!memberCourse.isActive && !memberCourse.completedAt) {
                        return reject({data: {isActive: false}})
                    }

                    commit(types.UPDATE_ENROLLED_COURSE, {courseSlug, memberCourse});
                    commit('course/UPDATE_COURSES_CONTENT', {courseSlug, courseData: course}, {root: true});

                    /**
                     * Member course has different statuses but we only save recent course
                     * if member is still in progress on that course.
                     */
                    if (memberCourse.status === 'In Progress') {
                        dispatch('member.details/saveRecentViewedCourse', {course}, {root: true});
                    }

                    resolve(data);
                })
                .catch(({response}) => reject(response))
                .done(_ => commit(types.SET_PROCESSING_STATE, false));
        });
    },

    /**
     * Enrol course.
     *
     * @param commit
     * @param hashedId
     * @returns {Promise}
     */
    enrolCourse({commit}, {hashedId}) {
        commit(types.SET_PROCESSING_STATE, true);

        return new Promise((resolve, reject) => {
            axios.post(api.enrolCourse(hashedId))
                .then(({data}) => {
                    commit(types.UPDATE_ASSESSMENT, {assessment: data.memberAssessment.data, hashedId});

                    /**
                     * Since member catalogue caching so we have to reset the inProgress courses.
                     * Because on enrol new course, in-progress courses listing is the only that affected.
                     */
                    commit(types.UPDATE_CATALOGUE_DATA, {status: 'inProgress', data: []});

                    resolve(data.message);
                })
                .catch(({response}) => {
                    /**
                     * If statusCode is 409 (ie. Has already enrolled) then we silently resolve the promise.
                     */
                    if (response.data.statusCode === 409) {
                        resolve();
                    } else {
                        reject(response);
                    }
                })
                .done(_ => commit(types.SET_PROCESSING_STATE, false));
        });
    },

    getCatalogue({commit, state}, params) {
        return new Promise((resolve, reject) => {
            if (shouldGetCatalogueFromCache(state, params)) return resolve(state.catalogue[params.status].data);

            commit(types.SET_PROCESSING_STATE, true);

            axios.get(api.getCatalogue(_.assign(params, {orderBy: state.orderBy})))
                .then(({data}) => {
                    commit(types.UPDATE_CATALOGUE_PAGINATION, {status: params.status, pagination: data.catalogue.meta.pagination});

                    const catalogueData = data.catalogue.meta.pagination.current_page === 1
                        ? data.catalogue.data
                        : [...state.catalogue[params.status].data, ...data.catalogue.data];

                    commit(types.UPDATE_CATALOGUE_DATA, {status: params.status, data: _.uniqBy(catalogueData, course => course.id)});
                    resolve(data);
                })
                .catch(({response}) => reject(response))
                .done(_ => commit(types.SET_PROCESSING_STATE, false));
        });
    },

    archiveCourse({commit, state}, {archiveCourse}) {
        commit(types.SET_PROCESSING_STATE, true);
        return new Promise((resolve, reject) => {
            axios.patch(api.archiveCourse(archiveCourse.id))
                .then(_ => {
                    // Remove archived course from inProgress catalogue
                    commit(
                        types.UPDATE_CATALOGUE_DATA,
                        {status: 'inProgress', data: state.catalogue.inProgress.data.filter(course => course.id !== archiveCourse.id)}
                    );

                    // Clear Archived courses to force re-fetch data
                    // to avoid loading data from caching.
                    commit(types.UPDATE_CATALOGUE_DATA, {status: 'archived', data: []});

                    resolve();
                })
                .catch(({response}) => reject(response))
                .done(_ => commit(types.SET_PROCESSING_STATE, false));
        })
    },

    unarchiveCourse({commit, state}, {hashedId}) {
        return new Promise((resolve, reject) => {
            commit(types.SET_PROCESSING_STATE, true);
            axios.patch(api.unarchiveCourse(hashedId))
                .then(({data}) => {
                    // Reset catalogue data so it's gonna fetch data from server again
                    // To make sure user can see course updates.
                    commit(types.UPDATE_CATALOGUE_DATA, {status: 'archived', data: []});
                    commit(types.UPDATE_CATALOGUE_DATA, {status: 'inProgress', data: []});
                    resolve(data);
                })
                .catch(({response}) => reject(response))
                .done(_ => commit(types.SET_PROCESSING_STATE, false));
        })
    },

    getCourseResults({commit, state}, {page = 1}) {
        return new Promise((resolve, reject) => {
            if (state.results.data.length && state.results.pagination.current_page === page) {
                return resolve({cached: true});
            }

            commit(types.SET_PROCESSING_STATE, true);
            axios.get(api.getCourseResults(page))
                .then(({data}) => {
                    commit(types.UPDATE_COURSE_RESULTS, {data: data.results.data, pagination: data.results.meta.pagination});
                    resolve(data);
                })
                .done(_ => commit(types.SET_PROCESSING_STATE, false));
        });
    },

    getCompletedCourses({commit, state}) {
        return new Promise((resolve, reject) => {
            // if (state.completedCourses) return state.completedCourses

            axios.get(api.getCompletedCourses())
                .then(({data}) => {
                    commit(types.UPDATE_COMPLETED_COURSES, data.results.data);
                    resolve(data);
                })
        });
    },

    changeOrderBy({commit, state}, criteria) {
        commit(types.SET_ORDER_BY, criteria);

        Object.keys(state.catalogue).forEach(status => {
            state.catalogue[status].data = [];
        })
    }
};

const mutations = {
    [types.UPDATE_ASSESSMENT](state, {assessment, hashedId}) {
        state.assessments = {...state.assessments, [hashedId]: assessment};
    },

    [types.UPDATE_ENROLLED_COURSE](state, {courseSlug, memberCourse}) {
        state.enrolledCourses = {...state.enrolledCourses, [courseSlug]: memberCourse};
    },

    [types.SET_CATALOGUE](state, {status, data, pagination}) {
        state.catalogue[status].data = data;
        state.catalogue[status].pagination = pagination;
    },

    [types.SET_COURSES_ENROLLED](state, data) {
        state.coursesEnrolled = data;
    },

    [types.UPDATE_CATALOGUE_DATA](state, {status, data}) {
        state.catalogue[status].data = data;
    },

    [types.UPDATE_CATALOGUE_PAGINATION](state, {status, pagination}) {
        state.catalogue[status].pagination = pagination;
    },

    [types.UPDATE_COURSE_RESULTS](state, {data, pagination}) {
        state.results.data = [...state.results.data, ...data];
        state.results.pagination = pagination;
    },

    [types.SET_ORDER_BY](state, order) {
        state.orderBy = order;
    },

    [types.SET_PROCESSING_STATE](state, isProcessing) {
        state.isProcessing = isProcessing;
    },

    [types.UPDATE_COMPLETED_COURSES](state, courses) {
        state.completedCourses = courses
    },
};

export default {
    namespaced: true,
    getters,
    state,
    actions,
    mutations
};
